import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { CardData } from '../../utils/chatApi';

interface CardMessageProps {
  cardData: CardData;
  isMine: boolean;
}

/**
 * Renders a rich card message in the chat.
 * Used for sharing pets, encounters, appointments, etc.
 */
export const CardMessage: React.FC<CardMessageProps> = ({ cardData, isMine }) => {
  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'pet':
        return 'Pet';
      case 'encounter':
        return 'Encounter';
      case 'appointment':
        return 'Appointment';
      case 'task':
        return 'Task';
      default:
        return 'Shared';
    }
  };

  return (
    <Link
      to={cardData.link}
      className={`
        block w-full max-w-xs rounded-xl overflow-hidden border transition-transform hover:scale-[1.02]
        ${isMine 
          ? 'bg-blue-700 border-blue-600' 
          : 'bg-white dark:bg-dark-bg-tertiary border-gray-200 dark:border-dark-border'
        }
      `}
    >
      {/* Type Badge */}
      <div className={`px-3 py-1.5 text-xs font-medium flex items-center justify-between ${
        isMine 
          ? 'bg-blue-800 text-blue-200' 
          : 'bg-gray-100 dark:bg-dark-bg-secondary text-gray-600 dark:text-dark-text-secondary'
      }`}>
        <span>{getTypeLabel(cardData.type)}</span>
        <ExternalLink className="w-3 h-3" />
      </div>

      {/* Card Content */}
      <div className="p-3 flex items-center gap-3">
        {/* Image */}
        {cardData.image_url ? (
          <img
            src={cardData.image_url}
            alt={cardData.title}
            className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-white dark:border-dark-border shadow-sm"
          />
        ) : (
          <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 border-2 shadow-sm ${
            isMine 
              ? 'bg-blue-600 border-blue-500 text-blue-200' 
              : 'bg-gray-200 dark:bg-gray-700 border-white dark:border-dark-border text-gray-500 dark:text-gray-400'
          }`}>
            <span className="text-xl font-bold">
              {cardData.title.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className={`font-semibold truncate ${
            isMine ? 'text-white' : 'text-gray-900 dark:text-dark-text-primary'
          }`}>
            {cardData.title}
          </p>
          {cardData.subtitle && (
            <p className={`text-sm truncate ${
              isMine ? 'text-blue-200' : 'text-gray-500 dark:text-dark-text-secondary'
            }`}>
              {cardData.subtitle}
            </p>
          )}
          
          {/* Tags */}
          {cardData.tags && cardData.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {cardData.tags.slice(0, 2).map((tag, idx) => (
                <span
                  key={idx}
                  className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    isMine 
                      ? 'bg-blue-600 text-blue-100' 
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  }`}
                >
                  {tag}
                </span>
              ))}
              {cardData.tags.length > 2 && (
                <span className={`text-xs ${
                  isMine ? 'text-blue-200' : 'text-gray-400'
                }`}>
                  +{cardData.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default CardMessage;

